import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Navigation, Building2, User, Phone, Mail, MapPin, ArrowRight, ArrowLeft } from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Company info
    company_name: "",
    company_address: "",
    company_phone: "",
    // Admin user info
    admin_name: "",
    admin_email: "",
    admin_password: "",
    admin_phone: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/register-company`, formData);
      const { token, user } = response.data;
      
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
      
      toast.success(`Welcome, ${user.name}! Your company has been registered.`);
      navigate("/employer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.company_name) {
        toast.error("Company name is required");
        return;
      }
    }
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute top-0 -left-40 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      <Card className="relative z-10 w-full max-w-lg border-0 shadow-2xl bg-slate-900/80 backdrop-blur-xl border border-white/10">
        <CardHeader className="space-y-1 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Navigation className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-black text-white">There You Are</span>
          </div>
          <CardTitle className="text-2xl font-bold text-white">Register Your Company</CardTitle>
          <CardDescription className="text-slate-400">
            {step === 1 
              ? "Enter your company details to get started" 
              : "Create your admin account"
            }
          </CardDescription>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 pt-4">
            <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-cyan-500' : 'bg-slate-700'}`} />
            <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-cyan-500' : 'bg-slate-700'}`} />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>Company Info</span>
            <span>Admin Account</span>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 1 && (
              <>
                {/* Company Name */}
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-slate-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Company Name *
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    placeholder="ABC Transportation Services"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Company Address */}
                <div className="space-y-2">
                  <Label htmlFor="company_address" className="text-slate-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Company Address
                  </Label>
                  <Input
                    id="company_address"
                    name="company_address"
                    placeholder="123 Main Street, City, State 12345"
                    value={formData.company_address}
                    onChange={handleChange}
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Company Phone */}
                <div className="space-y-2">
                  <Label htmlFor="company_phone" className="text-slate-300 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Company Phone
                  </Label>
                  <Input
                    id="company_phone"
                    name="company_phone"
                    placeholder="(555) 123-4567"
                    value={formData.company_phone}
                    onChange={handleChange}
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                <Button
                  type="button"
                  onClick={nextStep}
                  className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/30"
                >
                  Next: Create Admin Account
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                {/* Admin Name */}
                <div className="space-y-2">
                  <Label htmlFor="admin_name" className="text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Your Name *
                  </Label>
                  <Input
                    id="admin_name"
                    name="admin_name"
                    placeholder="John Doe"
                    value={formData.admin_name}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Admin Email */}
                <div className="space-y-2">
                  <Label htmlFor="admin_email" className="text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </Label>
                  <Input
                    id="admin_email"
                    name="admin_email"
                    type="email"
                    placeholder="admin@company.com"
                    value={formData.admin_email}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Admin Password */}
                <div className="space-y-2">
                  <Label htmlFor="admin_password" className="text-slate-300">
                    Password *
                  </Label>
                  <PasswordInput
                    id="admin_password"
                    name="admin_password"
                    data-testid="register-password-input"
                    placeholder="••••••••"
                    value={formData.admin_password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Admin Phone */}
                <div className="space-y-2">
                  <Label htmlFor="admin_phone" className="text-slate-300 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Your Phone
                  </Label>
                  <Input
                    id="admin_phone"
                    name="admin_phone"
                    placeholder="(555) 987-6543"
                    value={formData.admin_phone}
                    onChange={handleChange}
                    className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1 h-12 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/30"
                  >
                    {isLoading ? "Creating..." : "Create Company"}
                  </Button>
                </div>
              </>
            )}
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800 text-center">
            <p className="text-slate-400">
              Already have an account?{" "}
              <Link to="/login" className="text-cyan-400 font-semibold hover:text-cyan-300">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
